import { useMemo } from "react";
import {
  StartGuideReadinessTaskId,
  StartGuideState,
} from "@/features/pathpick/features/start-guide/types";

export interface StartGuideReadinessTask {
  id: StartGuideReadinessTaskId;
  isCompleted: boolean;
}

export interface StartGuideReadiness {
  tasks: StartGuideReadinessTask[];
  doneCount: number;
  totalCount: number;
  isAllDone: boolean;
  // 다음 단계(판매할 상품 찾기)로 넘어갈 수 있는지 — 할 일이 애초에 없는 KREAM 전용도 포함한다.
  isReadyForNextStep: boolean;
  // KREAM 판매 방식이면 사전 확인도 설정 목록도 통째로 건너뛴다(전달서 §5).
  needsSmartStoreSetup: boolean;
}

/**
 * 판매 방식으로부터 이번 사용자에게 필요한 설정 태스크만 계산한다.
 *
 * API/엑셀 조건부 항목이 뒤바뀌면 안 되는 게 이 훅의 핵심 계약이다(전달서 §24):
 * API 방식은 `api_setting`만, 엑셀 방식은 `delivery_template`만 갖는다.
 */
export default function useStartGuideReadiness(
  state: StartGuideState
): StartGuideReadiness {
  return useMemo(() => {
    const needsSmartStoreSetup = state.salesMode !== "kream";

    const ids: StartGuideReadinessTaskId[] = [];
    if (needsSmartStoreSetup) {
      ids.push("product_type", "overseas_permission", "overseas_release");
      ids.push(state.salesMode === "api" ? "api_setting" : "delivery_template");
      // 판매자 기본 정보는 앞 단계에서 확인한 값을 입력하는 자리라 항상 마지막이다.
      ids.push("buyer_info");
    }

    const tasks = ids.map((id) => ({
      id,
      isCompleted: state.completedTasks.includes(id),
    }));
    const doneCount = tasks.filter((task) => task.isCompleted).length;

    return {
      tasks,
      doneCount,
      totalCount: tasks.length,
      // 태스크가 0개(KREAM 전용)인 경우를 "전부 완료"로 보면 안 된다 — 진행률 문구가 달라진다.
      isAllDone: tasks.length > 0 && doneCount === tasks.length,
      // 다만 이동은 막지 않는다 — 할 일이 없는 사용자도 다음 단계 CTA 는 필요하다.
      isReadyForNextStep: tasks.length === 0 || doneCount === tasks.length,
      needsSmartStoreSetup,
    };
  }, [state.salesMode, state.completedTasks]);
}
